// passive-integer-fold.ts — Janus C-PI (board row C-PI; plans/passive-integer-layers-plan-2026-09-25.md §3/§6a): the integer layers a
// creature's HELD, active, level-unlocked `passive_ability` entries carry, folded into its compiled `computed.creature`. PHP's integer
// chokepoint (process_int_layer_types) reads host + default-provider carriers, statuses and equipped gear — never passives — so these
// families were DEAD on every creature. The native BASE compiler folds them before paint/write-back;
// incremental action compilers retain only the affected clusters. There is no separate CPI claim transport.
//
// Eligibility (the compile's own passive rules, plan §3): the entry's `passive_conditions_active` stamp (PHP truthiness — a stamped
// falsy value is inactive, an unstamped entry is active), the PC class-level lock (`levelGate`, carrier attribution, run BEFORE the
// post-finalize level filter), and three exclusions — feat-granted entries (`ft_granted_by_feat`: the feat fold counts them), an
// equipped gear row's own nested passive (`ft_passive_entry_is_gear_nested`: the equipped walk counts it, unless it carries a bracket
// override) and trigger-keyed passives (`contains_trigger_key`: the triggered arm applies them). One occurrence per passive id; a
// multiclass PC keeps an UNLOCKED occurrence (twin of dynamic-integer-sources.ts). An unsuppressed fixed equipped value (`{x}` lock,
// an inventory_equipped source row with value_brace) overrides the family: the fold contributes nothing.
//
// IDEMPOTENT by construction. The fold records what it added in `computed.creature.cpi` (family → folded total) and marks its source
// rows `fold: 'cpi'`; `stripPassiveIntegerFold` takes exactly that back. The record is a sibling key no rollup touches, so a PHP or
// browser re-rollup that stale-reads the folded total back as the host base (layer_entries falls back to computed.creature.modifiers)
// and rewrites modifier_sources still strips correctly. Legacy R-041 rows (`aura_distance_mod`, kind passive_ability, unmarked — the
// retired PHP fold) are taken back the same way.
import type { ItemFieldRow } from './item-field-reader';
import { isAuraEffect } from '../ability/aoe/aura-classification';
import type { AbilityEssenceSourceDependencies } from './ability-essence-sources';
import { createAbilityEssenceSourceCollector } from './ability-essence-sources';
import { createFormulaReadContext } from './formula-read-context';
import { collectEquippedInventoryRows } from './equipped-integer-contribution';
import type { EquippedIntegerDependencies } from './equipped-integer-contribution';
import { containsAbilityTriggerKey, readAbilityEntryId } from './inventory-ability-grant-helpers';
import { readInventoryField, readItemUuidSlot, readLayerListEntryUuid } from './inventory-raw-readers';
import { layerEntryNeedsResolve, resolveLayerEntry } from './layer-entry-resolver';
import { integerFragmentBraces } from './status-integer-contribution';
import { phpJsonString, phpJsonTruthy } from './php-json-values';
import { capInventorySourceRows } from './computed-persistence';
import { compileCreatureDerivedJump } from './creature-derived-jump';
import { applyCreatureSpeedFactorRows, statusDefinitionShieldsSpeed } from './creature-speed-factor';
import { readLayerScoringToken, readNodeItemId } from './layer-cell-readers';
import { createCatalogDefaultProviderReader } from './provider-catalog-order';
import { createLayerEntriesReader } from './layer-entries-reader';
import { createInventoryReadContext } from './inventory-read-context';
import { readCreaturePayloadStatuses } from './creature-status-reader';
import { carryingCapacityFactor, compiledCapacityFactor, rescaleCompiledCapacity } from './creature-carrying-capacity';
import type { CreatureSpeedSource } from '../types';

type Row = Record<string, unknown>;
const isObj = (v: unknown): v is Row => v !== null && typeof v === 'object' && !Array.isArray(v);
const arrayLike = (v: unknown): v is ItemFieldRow => v !== null && typeof v === 'object';
const own = (v: unknown, key: string): unknown => arrayLike(v) && Object.prototype.hasOwnProperty.call(v, key) ? (v as Row)[key] : undefined;
const upper = (v: unknown): string => typeof v === 'string' ? v.toUpperCase() : '';
const validUuid = (v: string): boolean => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v);
const int = (v: unknown): number => { const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v) : 0; return Number.isFinite(n) ? Math.trunc(n) : 0; };

/** The families this fold owns (C-PI slice 0: the owner's pilot batch + the retired R-041 aura reach). A family joins when its census is
 *  signed off — no runtime switch. Every family here lands in computed.creature.modifiers[<name>] (the chokepoint's slot). */
export const CPI_MODIFIER_FAMILIES: readonly string[] = ['max_summons', 'bright_light_dim_light', 'ascension_skill_point_range_mod', 'aura_distance_mod', 'arcane_threshold',
    // family 4 (adv / dis + contesting): consumed through the modifiers bridge (skill_roll_inc.php, the defenses tab)
    'contesting_roll_adv_vs_spells', 'contesting_rolls_mod', 'skill_rolls_adv', 'skill_rolls_dis',
    // family 5 (percentages / multiplier): additive through the same bridge (calc_total_damage_and_apply_to_target, janus.php throw / range)
    'all_damage_output_changed_by_percentage', 'all_incoming_damage_changed_by_percentage', 'throwing_distance_changed_by_percentage', 'weapon_ranged_attack_range_multiplier'];
/** C-PI family 1 (AC + mitigation): update_ac_values' parts, not modifiers slots. ac_mod_* fold into ac.total_mod / ac.ac / ac.dr (record
 *  key `ac`); damage_mitigation into ac.mitigation (+ computed.creature.damage_mitigation on a PC — never invented on a non-PC row). */
export const CPI_AC_FAMILIES: readonly string[] = ['ac_mod_magical', 'ac_mod_non_magical'];
export const CPI_MITIGATION_FAMILY = 'damage_mitigation';
/** C-PI family 2: speeds on a NON-PC row (computed.creature.speeds.<short>; record key = the family name). */
export const CPI_SPEED_FAMILIES: readonly string[] = ['speed_walking', 'speed_swimming', 'speed_climbing', 'speed_flying', 'speed_burrowing', 'speed_hovering', 'teleport'];
/** C-PI family 6: jumping (computed.creature.speeds.jumping; record key `speed_jumping`) — on every row, PC or not. */
export const CPI_JUMP_FAMILY = 'speed_jumping';
/** C-PI family 7: the highest buff × all penalties from host, carriers, held passives (feat / gear included), feats and statuses
 *  scales computed.creature.capacity (record key = the family name = the collected factor).
 *  No PHP compile reads the layer (owner: the compile is a front-end system); native BASE and affected-cluster action compilers carry the capacity cell. */
export const CPI_CAPACITY_FAMILY = 'carrying_capacity_multiplier';
/** C-PI family 8 (Wizard v9 "Ever-Changing Form", 2026-10-09): the SPEED SHIELD — a creature holding a speed_cannot_be_reduced status gets
 *  its whole computed.creature.speeds re-derived by the browser speed compile (compileCreatureSpeeds, shielded), whichever path compiled the
 *  row. Each replaced wrapper keeps the unshielded one under `shield_prior` (null = absent) so the strip restores PHP's exactly; it applies
 *  only when the UNSHIELDED twin reproduces the stored speeds (the parity guard). Record key = the family name (1). */
export const CPI_SPEED_SHIELD_FAMILY = 'speed_shield';
export const CPI_FOLD_FAMILIES: readonly string[] = [...CPI_MODIFIER_FAMILIES, ...CPI_AC_FAMILIES, CPI_MITIGATION_FAMILY, ...CPI_SPEED_FAMILIES, CPI_JUMP_FAMILY, CPI_CAPACITY_FAMILY, CPI_SPEED_SHIELD_FAMILY];
/** computed.creature[CPI_RECORD_KEY] = { <family>: <folded passive total> } — only families the fold changed (non-zero). */
export const CPI_RECORD_KEY = 'cpi';
export const CPI_FOLD_MARK = 'cpi';
/** A family's compiled default when nothing authors it (finalize_creature_arr: `meta.max_summons = total ?: 3`). A passive's signed "+N" is
 *  additive over that default ("The default is 3 for any creature" — Minion Mastery), so an unfolded total of 0 folds from the default. */
export const CPI_FAMILY_BASELINE: Readonly<Record<string, number>> = { max_summons: 3 };
/** C-PI family 3 (owner default rule, 2026-09-27): arcane_threshold = 1 + the sum of EVERY source (its sources are all "+N"). PHP hard-sets
 *  spellcasting.arcane_threshold = 1 and the modifiers bridge then serves the bare sum (a +1 feat / gear read 1, not 2) — the fold adds the 1
 *  whenever the sources sum non-zero, passive or not; a {N} brace replaces. spellcasting.arcane_threshold follows (total ?: 1). */
export const CPI_FAMILY_OFFSET: Readonly<Record<string, number>> = { arcane_threshold: 1 };
/** The legacy PHP fold (R-041) wrote unmarked kind=passive_ability rows for this one family. */
const LEGACY_R041_FAMILY = 'aura_distance_mod';

export type PassiveIntegerFoldDependencies = AbilityEssenceSourceDependencies & Pick<EquippedIntegerDependencies, 'resolveInventoryInstance'> & {
    /** BASE-confirmed deletions author nothing, including stale inline carriers. */
    missingDefinitionIds?: readonly string[];
    /** family 8: the browser speed compile over the host (shield true = speed_cannot_be_reduced honoured, false = the PHP-equal twin). Absent
     *  → the speed-shield family is not folded (the caller cannot run the speed compile). */
    compileSpeeds?: (host: Row, shield: boolean) => { ok: true; speeds: Row } | { ok: false; reason: string };
};
/** `total` = the additive share; `brace` = the largest `{N}` a passive authors (return_int_score: a brace REPLACES the total, max wins). */
export interface PassiveIntegerContribution { total: number; brace: number | null; sources: Row[] }
export interface PassiveIntegerCollection {
    byFamily: Record<string, PassiveIntegerContribution>;
    /** Every passive definition id the walk read (eligible or not) — the closure a heal claim binds its revision tokens to. */
    passiveIds: string[];
    /** the host is a player character (computed.creature.damage_mitigation mirrors ac.mitigation only there) */
    isPc?: boolean;
    /** family 6: every dynamic jump source (passive, status, equipped, feat) — PHP's jump branches read none of them */
    jump?: JumpSources;
    /** family 7: the collected carrying_capacity_multiplier (1 = none) and every source that authors one */
    capacity?: CapacitySources;
    /** families 2 + 6: a held status carries speed_cannot_be_reduced (Wizard v9 "Ever-Changing Form") — reducing fold rows are ignored */
    speedShield?: boolean;
    /** family 8: the shielded and the unshielded (PHP-equal) speed compiles of the host — only while a held status shields */
    shieldSpeeds?: { shielded: Row; unshielded: Row };
}
/** `present`: the layer is authored on ANY source the row holds (any value, any eligibility) — R-209's ownership test, the twin of PHP's
 *  ft_encumbrance_weight_tiers_browser_owned() presence check. */
export interface CapacitySources { factor: number; sources: Row[]; incomplete: boolean; missing: string[]; present: boolean }

/** Compile readers share the cast classifier's presence semantics. */
export function isAuraPassive(def: ItemFieldRow, layerValue: (row: ItemFieldRow | null, name: string) => unknown): boolean {
    return isAuraEffect({ read: { value: layerValue } }, def);
}

/** The held passive entries of a row: the ROOT bucket (non-PC rows keep it there; the in-compile PC) plus the relocated PC bucket
 *  (computed.creature.abilities.passive_ability), root first — PHP's layer_entries bridge order. */
export function heldPassiveEntries(host: Row, readers: { layerEntries: (row: ItemFieldRow | null, name: string) => ItemFieldRow }): unknown[] {
    const out: unknown[] = [...Object.values(readers.layerEntries(host as ItemFieldRow, 'passive_ability'))];
    const computed = own(own(own(host, 'computed'), 'creature'), 'abilities');
    const relocated = own(computed, 'passive_ability');
    if (arrayLike(relocated)) for (const e of Object.values(relocated)) if (!out.includes(e)) out.push(e);
    return out;
}

/** Collect each family's passive contribution for a host row. Pure: reads only through `deps`. */
export function collectPassiveIntegerContributions(host: Row, deps: PassiveIntegerFoldDependencies, families: readonly string[] = CPI_FOLD_FAMILIES): PassiveIntegerCollection {
    const context = createFormulaReadContext(deps);
    const abilities = createAbilityEssenceSourceCollector(deps);
    const query = (id: string): ItemFieldRow | null => deps.queryFullItem(id);
    const readers = { ...context, queryFullItem: query, resolveCarrierRef: (id: string) => deps.resolveCarrierRef(id), itemName: (id: string) => deps.resolveItemName(id) };
    const byFamily: Record<string, PassiveIntegerContribution> = {};
    for (const f of families) byFamily[f] = { total: 0, brace: null, sources: [] };
    const passiveIds = new Set<string>();
    const hostRow = host as ItemFieldRow;
    const levelGate = abilities.levelGate(hostRow);
    const idOf = (entry: unknown): string => upper(readAbilityEntryId(entry, readers) || readLayerListEntryUuid(entry));
    const defOf = (entry: unknown): ItemFieldRow | null => {
        // the token-bound definition (store / catalog) wins over an inline copy — the claim binds ITS revision (Codex review 2026-09-26)
        const id = idOf(entry);
        if (validUuid(id)) { const q = query(id); if (arrayLike(q)) return q; }
        const unwrapped = arrayLike(own(entry, 'item_arr')) ? own(entry, 'item_arr') : entry;
        let def: unknown = unwrapped;
        if (typeof unwrapped === 'string') def = null;
        else if (arrayLike(unwrapped) && !arrayLike(own(unwrapped, 'item_layer_types'))) def = layerEntryNeedsResolve(unwrapped) ? resolveLayerEntry(unwrapped, hostRow, query) : null;
        return arrayLike(def) ? def : null;
    };
    const held = heldPassiveEntries(host, context);
    // a passive an EQUIPPED gear row surfaced (its `source` = that row) is the equipped walk's share (ft_equipped_gear_nested_passive_keys:
    // the row's resolved passive list — template + enchantment) — by ORIGIN, so no template read is needed
    const gearRows = new Set<string>();
    if (held.length) {
        for (const row of collectEquippedInventoryRows(hostRow, readers as never)) { const id = upper(phpJsonString(readInventoryField(row as never, 'inventory_id') ?? '')); if (id) gearRows.add(id); }
        const walk = (list: unknown): void => {
            for (const inst of (Array.isArray(list) ? list : isObj(list) ? Object.values(list) : []).filter(isObj)) {
                if (readItemUuidSlot(readInventoryField(inst as never, 'equipped') ?? '') !== '') { const id = upper(phpJsonString(readInventoryField(inst as never, 'inventory_id') ?? '')); if (id) gearRows.add(id); }
                walk(inst.inventory_in_container);
            }
        };
        walk(own(host, 'inventory'));
    }
    // ELIGIBILITY FIRST (stamp, feat, trigger, PC level lock), then one occurrence per id — a non-gear occurrence preferred (Codex review:
    // deduping first could keep an ineligible copy and drop the eligible one)
    interface Candidate { entry: unknown; def: ItemFieldRow; gear: boolean; feat: boolean }
    const chosen = new Map<string, Candidate>();
    const anonymous: Candidate[] = [];
    for (const entry of held) {
        const id = idOf(entry);
        if (id !== '') passiveIds.add(id);
        if (arrayLike(entry) && Object.prototype.hasOwnProperty.call(entry, 'passive_conditions_active') && !phpJsonTruthy(own(entry, 'passive_conditions_active'))) continue;
        // (a feat-granted passive is the feat fold's for the chokepoint families — kept here only for the jump resolver, which no walk covers)
        const feat = arrayLike(entry) && phpJsonTruthy(own(entry, 'ft_granted_by_feat'));
        const def = defOf(entry);
        if (!def) continue;
        const defId = upper(phpJsonString(context.itemField(def, 'item_id') ?? ''));
        if (validUuid(defId)) passiveIds.add(defId);
        if (containsAbilityTriggerKey(def)) continue;
        // an AURA passive (check_item_arr_aura_effect: the aoe_type + aoe_distance + persistent_aoe + aoe_follows_target quartet, each
        // layer_value !== null) is never an always-on fold: it reaches its holder only through the status its zone applies (a same-team
        // aura's own anchor row, already scored by the status walk; an opposing-team aura's self-anchor delivers nothing). PHP's static
        // passive folds skip it on purpose (finalize_creature_arr's feat/passive fold, ft_compute_fold_carrier_granted_ability_defenses);
        // without this the browser gave a holder its own aura's share — Brilliant Corona's +30 bright_light_dim_light on a Sun Dragon
        // (2026-10-07). Every family, jump included (its resolver reads the status rows itself).
        if (isAuraPassive(def, context.layerValue)) continue;
        if (levelGate !== null && !levelGate(entry, def)) continue;
        const cand = { entry, def, feat, gear: arrayLike(entry) && gearRows.has(upper(phpJsonString(own(entry, 'source') ?? ''))) };
        if (id === '') { anonymous.push(cand); continue; }
        const prev = chosen.get(id);
        if (!prev || ((prev.gear || prev.feat) && !cand.gear && !cand.feat)) chosen.set(id, cand);
    }
    const jump = families.includes(CPI_JUMP_FAMILY) ? collectJumpSources(host, [...chosen.values(), ...anonymous], context, query) : undefined;
    const capacity = families.includes(CPI_CAPACITY_FAMILY) ? collectCapacitySources(host, [...chosen.values(), ...anonymous], context, query, deps,
        held.map(e => defOf(e)).filter((d): d is ItemFieldRow => d !== null)) : undefined;
    const speedShield = families.some(f => CPI_SPEED_FAMILIES.includes(f) || f === CPI_JUMP_FAMILY || f === CPI_SPEED_SHIELD_FAMILY) && heldStatusShieldsSpeed(host, context, query);
    let shieldSpeeds: PassiveIntegerCollection['shieldSpeeds'];
    if (speedShield && families.includes(CPI_SPEED_SHIELD_FAMILY) && deps.compileSpeeds && isObj(own(own(host, 'computed'), 'creature'))) {
        const shielded = deps.compileSpeeds(host, true), unshielded = deps.compileSpeeds(host, false);
        if (shielded.ok && unshielded.ok) shieldSpeeds = { shielded: shielded.speeds, unshielded: unshielded.speeds };
    }
    const candidates = [...chosen.values(), ...anonymous].filter(c => !c.feat);
    if (!candidates.length) return { byFamily, passiveIds: [...passiveIds], isPc: arrayLike(own(host, 'player_characters')), jump, capacity, speedShield, shieldSpeeds };
    for (const family of families) {
        // a gear-surfaced passive stays the equipped walk's share unless it authors a bracket override FOR THIS FAMILY (per family: a
        // brace in one family must not admit the passive's additive share of another — Codex review)
        const entries = candidates.filter(c => !c.gear || integerFragmentBraces(Object.values(context.layerEntries(c.def, family))).length > 0)
            .map(c => arrayLike(c.entry) ? { ...(c.entry as Row), item_arr: c.def } : c.def);
        if (!entries.length) continue;
        const out = abilities.namedEntries(hostRow, entries as unknown as ItemFieldRow, family, 'passive_ability', true, undefined, null);
        for (const line of out.lines) {
            const value = int(line.value);
            const c = byFamily[family]!;
            if (line.value_brace !== undefined && line.value_brace !== null) {
                const brace = int(line.value_brace);
                c.brace = c.brace === null ? brace : Math.max(c.brace, brace);
                c.sources.push({ category: 'passive_ability', label: line.label, value: brace, href_id: line.href_id ?? '', kind: 'passive_ability', value_brace: brace, fold: CPI_FOLD_MARK });
                continue;
            }
            if (value === 0) continue;
            c.total += value;
            c.sources.push({ category: 'passive_ability', label: line.label, value, href_id: line.href_id ?? '', kind: 'passive_ability', fold: CPI_FOLD_MARK });
        }
    }
    return { byFamily, passiveIds: [...passiveIds], isPc: arrayLike(own(host, 'player_characters')), jump, capacity, speedShield, shieldSpeeds };
}

/** speed_cannot_be_reduced on a HELD target status (its own layer, or a passive the status attaches). A status the page cannot resolve
 *  does not shield (fail toward the ordinary, reduced value). */
function heldStatusShieldsSpeed(host: Row, context: JumpReaders, query: (id: string) => ItemFieldRow | null): boolean {
    const t = own(own(host, 'statuses'), 'target');
    const rows = Array.isArray(t) ? t.filter(arrayLike) : arrayLike(t) ? Object.values(t).filter(arrayLike) : [];
    const attached = (def: unknown): unknown[] => Object.values(context.layerEntries(def as ItemFieldRow, 'passive_ability'))
        .map(entry => { const id = upper(readLayerListEntryUuid(entry)); return validUuid(id) ? query(id) : arrayLike(entry) ? entry : null; });
    return rows.some(st => {
        const sid = upper(phpJsonString(own(st, 'status_item_id') ?? ''));
        const inline = own(st, 'status_arr');
        const def = arrayLike(inline) && arrayLike(own(inline, 'item_layer_types')) ? inline : validUuid(sid) ? query(sid) : null;
        return statusDefinitionShieldsSpeed(def, context.layerValue as never, attached);
    });
}

/** check_item_arr_aura_effect() is handled by the candidate filter; this is ft_passive_is_combat_start_timed(): a "for 1 minute after combat
 *  begins" passive reaches its holder only as the status row its trigger writes (the status walk finds it while held). */
const combatStartTimed = (def: ItemFieldRow, layerValue: (row: ItemFieldRow | null, name: string) => unknown): boolean =>
    layerValue(def, 'triggers_after_combat_begins') != null && layerValue(def, 'duration_type') != null;
/** Family 7: every carrying_capacity_multiplier source — the host (+ its default-provider carriers), each eligible held passive (feat- and
 *  gear-surfaced too: no PHP walk counts this layer), each feat's own layers, each target status. Highest buff wins; penalties multiply.
 *  A source definition the page does not hold makes the family incomplete (left unfolded — never a partial answer). */
function collectCapacitySources(host: Row, passives: Array<{ def: ItemFieldRow; gear: boolean; feat: boolean }>, context: JumpReaders,
    query: (id: string) => ItemFieldRow | null, deps: PassiveIntegerFoldDependencies, allPassives: ItemFieldRow[] = []): CapacitySources {
    const sources: Row[] = [];
    const missing = new Set<string>();
    const deleted = new Set((deps.missingDefinitionIds ?? []).map(upper));
    let incomplete = false, buff = 1, penalty = 1, present = false;
    // Capacity has no provider precedence: inspect every bucket entry on every
    // carrier using the shared readers, retaining the actual source identity.
    const local = createLayerEntriesReader({ nodeItemId: readNodeItemId, providers: () => [],
        inventoryContext: createInventoryReadContext(deps.resolveInventoryInstance),
        aggregationStrategy: () => 'append', abilityTypes: deps.abilityTypes });
    const resolveCarrierRef = (id: string): ItemFieldRow | null => {
        if (deleted.has(upper(id))) return null;
        const def = deps.resolveCarrierRef(id);
        if (!def) { incomplete = true; missing.add(upper(id)); }
        return def;
    };
    const providers = createCatalogDefaultProviderReader({ catalogRows: deps.catalogRows, nodeItemId: readNodeItemId, resolveCarrierRef });
    const authored = (def: ItemFieldRow | null): boolean => !!def && !deleted.has(upper(readNodeItemId(def) ?? ''))
        && context.layerValue(def, CPI_CAPACITY_FAMILY) != null;
    if (allPassives.some(authored)) present = true;
    const visited = new Set<string>();
    const take = (def: ItemFieldRow | null, kind: string, fallbackId = ''): void => {
        if (!def) return;
        const id = upper(readNodeItemId(def) ?? '');
        if (deleted.has(id)) return;
        if (id && visited.has(id)) return;
        if (id) visited.add(id);
        const entries = Object.values(local.read(def, CPI_CAPACITY_FAMILY));
        if (entries.length) present = true;
        for (const entry of entries) {
            const f = carryingCapacityFactor(readLayerScoringToken(entry));
            if (f === 1) continue;
            if (f < 1) penalty *= f; else buff = Math.max(buff, f);
            sources.push({ category: kind, label: phpJsonString(context.itemField(def, 'item_name') ?? '') || 'Unknown source', value: f,
                href_id: id || fallbackId, kind });
        }
        for (const carrier of providers.providers(def)) {
            // Inline carriers are references too: only the held definition can
            // author cells bound to its token, and a deleted id authors nothing.
            const carrierId = upper(readNodeItemId(carrier) ?? '');
            take(validUuid(carrierId) ? resolveCarrierRef(carrierId) : carrier, kind === 'host' ? 'carrier' : kind);
        }
    };
    take(host as ItemFieldRow, 'host');
    for (const c of passives) if (!combatStartTimed(c.def, context.layerValue)) take(c.def, c.gear ? 'inventory_equipped' : c.feat ? 'feat' : 'passive_ability');
    const featEntries = [...Object.values(context.layerEntries(host as ItemFieldRow, 'feat'))];
    const relocated = own(own(own(own(host, 'computed'), 'creature'), 'abilities'), 'feat');
    if (arrayLike(relocated)) for (const e of Object.values(relocated)) if (!featEntries.includes(e)) featEntries.push(e);
    const seenFeat = new Set<string>();
    for (const e of featEntries) {
        const fid = upper(typeof e === 'string' ? e : phpJsonString(own(e, 'item_id') ?? own(e, 'ability_id') ?? ''));
        if (!validUuid(fid) || seenFeat.has(fid)) continue;
        seenFeat.add(fid);
        if (deleted.has(fid)) continue;
        const def = query(fid);
        if (!def) { incomplete = true; missing.add(fid); continue; }
        take(def, 'feat');
    }
    const statusRows = (() => { const t = own(own(host, 'statuses'), 'target'); return Array.isArray(t) ? t.filter(arrayLike) : arrayLike(t) ? Object.values(t).filter(arrayLike) : []; })();
    for (const st of statusRows) {
        const sid = upper(phpJsonString(own(st, 'status_item_id') ?? ''));
        if (deleted.has(sid)) continue;
        const inline = own(st, 'status_arr');
        const def = arrayLike(inline) && arrayLike(own(inline, 'item_layer_types')) ? inline as ItemFieldRow : validUuid(sid) ? query(sid) : null;
        if (!def) { if (validUuid(sid)) { incomplete = true; missing.add(sid); } continue; }
        take(def, 'status_effect', sid);
    }
    return { factor: buff * penalty, sources, incomplete, missing: [...missing], present };
}

const sourcesOf = (creature: Row, family: string): Row[] => {
    const all = isObj(creature.modifier_sources) ? creature.modifier_sources as Row : null;
    const list = all ? all[family] : undefined;
    return Array.isArray(list) ? list.filter(isObj) : isObj(list) ? Object.values(list).filter(isObj) : [];
};
const isFoldRow = (r: Row, family: string, hasRecord: boolean): boolean => r.fold === CPI_FOLD_MARK
    || (family === LEGACY_R041_FAMILY && !hasRecord && r.kind === 'passive_ability' && r.category === 'passive_ability');
export const isPassiveIntegerFoldRow = isFoldRow;
function setModifier(creature: Row, family: string, total: number): void {
    const mods: Row = isObj(creature.modifiers) ? creature.modifiers as Row : {};
    if (total === 0) delete mods[family]; else mods[family] = total; // the chokepoint's zero-elision convention
    if (Object.keys(mods).length) creature.modifiers = mods; else delete creature.modifiers;
}
/** Cells derived from a family's total (finalize_creature_arr: `meta.max_summons = total ?: 3`) — kept in step whenever the fold moves it. */
function syncDerived(creature: Row, family: string, stripping = false): void {
    if (family === 'arcane_threshold') {
        const sc: Row = isObj(creature.spellcasting) ? creature.spellcasting as Row : (creature.spellcasting = {} as Row) as Row;
        // PHP hard-sets it to 1 (finalize_creature_arr) whatever the sources — the strip restores exactly that
        sc.arcane_threshold = stripping ? CPI_FAMILY_OFFSET.arcane_threshold! : int(isObj(creature.modifiers) ? (creature.modifiers as Row).arcane_threshold : 0) || CPI_FAMILY_OFFSET.arcane_threshold!;
        return;
    }
    if (family !== 'max_summons') return;
    const meta: Row = isObj(creature.meta) ? creature.meta as Row : (creature.meta = {} as Row) as Row;
    meta.max_summons = int(isObj(creature.modifiers) ? (creature.modifiers as Row).max_summons : 0) || CPI_FAMILY_BASELINE.max_summons!;
}
function setSources(creature: Row, family: string, rows: Row[]): void {
    const all: Row = isObj(creature.modifier_sources) ? creature.modifier_sources as Row : {};
    if (rows.length) all[family] = rows; else delete all[family];
    if (Object.keys(all).length) creature.modifier_sources = all; else delete creature.modifier_sources;
}

/** Take back exactly what a previous fold added (mutates `creature`, a computed.creature object). */
/** The two ac_mod families share one record (`ac`) and one row set: selecting either selects both (Codex review). */
function normalizeFamilies(families: readonly string[]): readonly string[] {
    let out = families.some(f => CPI_AC_FAMILIES.includes(f)) ? [...new Set([...families, ...CPI_AC_FAMILIES])] : [...families];
    if (out.some(f => CPI_SPEED_FAMILIES.includes(f) || f === CPI_JUMP_FAMILY || f === CPI_SPEED_SHIELD_FAMILY)) out = [...new Set([...out, ...CPI_SPEED_FAMILIES, CPI_JUMP_FAMILY, CPI_SPEED_SHIELD_FAMILY])];
    if (out.some(f => SKILL_GLOBAL_FAMILIES.includes(f))) out = [...new Set([...out, ...SKILL_GLOBAL_FAMILIES])];
    return out;
}
export function stripPassiveIntegerFold(creature: Row, families: readonly string[] = CPI_FOLD_FAMILIES): void {
    families = normalizeFamilies(families);
    const record = isObj(creature[CPI_RECORD_KEY]) ? creature[CPI_RECORD_KEY] as Row : null;
    if (families.some(f => CPI_AC_FAMILIES.includes(f))) stripAcFold(creature, record);
    if (families.includes(CPI_MITIGATION_FAMILY)) stripMitigationFold(creature, record);
    if (families.includes(CPI_JUMP_FAMILY)) stripJumpFold(creature);
    if (families.some(f => CPI_SPEED_FAMILIES.includes(f))) stripSpeedFold(creature, record);
    if (families.includes(CPI_SPEED_SHIELD_FAMILY)) stripSpeedShieldFold(creature);
    if (families.includes(CPI_CAPACITY_FAMILY)) stripCapacityFold(creature);
    if (families.some(f => SKILL_GLOBAL_FAMILIES.includes(f))) stripSkillGlobals(creature);
    for (const family of families.filter(f => CPI_MODIFIER_FAMILIES.includes(f))) {
        const hasRecord = !!record && Object.prototype.hasOwnProperty.call(record, family);
        const rows = sourcesOf(creature, family);
        const foldRows = rows.filter(r => isFoldRow(r, family, hasRecord));
        // the record holds the DELTA the fold applied (a brace or a baseline makes it differ from the rows' sum)
        const previous = hasRecord ? int(record![family]) : foldRows.reduce((s, r) => s + int(r.value), 0);
        if (previous !== 0) { setModifier(creature, family, int(isObj(creature.modifiers) ? (creature.modifiers as Row)[family] : 0) - previous); syncDerived(creature, family, true); }
        if (foldRows.length) setSources(creature, family, rows.filter(r => !isFoldRow(r, family, hasRecord)));
    }
    if (record) {
        for (const family of families) delete record[family];
        if (families.some(f => CPI_AC_FAMILIES.includes(f))) delete record.ac;
        if (families.includes(CPI_MITIGATION_FAMILY)) delete record.damage_mitigation_pc;
        if (!Object.keys(record).length) delete creature[CPI_RECORD_KEY];
    }
}

/** Add a collection onto an UNFOLDED computed.creature (mutates). Returns the record written. */
export function applyPassiveIntegerFold(creature: Row, collection: PassiveIntegerCollection, families: readonly string[] = CPI_FOLD_FAMILIES): Row {
    families = normalizeFamilies(families);
    const record: Row = isObj(creature[CPI_RECORD_KEY]) ? creature[CPI_RECORD_KEY] as Row : {};
    if (families.some(f => CPI_AC_FAMILIES.includes(f))) applyAcFold(creature, collection, record);
    if (families.includes(CPI_MITIGATION_FAMILY)) applyMitigationFold(creature, collection, record);
    if (families.includes(CPI_SPEED_SHIELD_FAMILY)) applySpeedShieldFold(creature, collection, record);
    if (families.some(f => CPI_SPEED_FAMILIES.includes(f))) applySpeedFold(creature, collection, record);
    if (families.includes(CPI_JUMP_FAMILY)) applyJumpFold(creature, collection, record);
    if (families.includes(CPI_CAPACITY_FAMILY)) applyCapacityFold(creature, collection, record);
    for (const family of families.filter(f => CPI_MODIFIER_FAMILIES.includes(f))) {
        const c = collection.byFamily[family] ?? { total: 0, brace: null, sources: [] };
        const base = int(isObj(creature.modifiers) ? (creature.modifiers as Row)[family] : 0);
        const offset = CPI_FAMILY_OFFSET[family];
        // an offset family folds whenever its sources sum non-zero, passive or not (arcane_threshold: 1 + every source)
        if (c.total === 0 && c.brace === null && !c.sources.length && !(offset !== undefined && base !== 0)) continue;
        const existing = sourcesOf(creature, family);
        // equipped {x} lock (the feat fold's rule): an unsuppressed fixed equipped value overrides everything else
        if (existing.some(r => r.kind === 'inventory_equipped' && r.value_brace !== undefined && r.value_brace !== null && !phpJsonTruthy(r.suppressed))) continue;
        // a braced skill global is not modelled on the sheet (a replacement of the previous global lines): left unfolded (Codex review)
        if (SKILL_GLOBAL_FAMILIES.includes(family) && c.brace !== null) continue;
        // an existing brace row (a status {N}) already REPLACED the total: no offset on top of it (Codex review)
        const bracedBase = existing.some(r => r.value_brace !== undefined && r.value_brace !== null && !phpJsonTruthy(r.suppressed));
        const sum = base + c.total;
        const next = c.brace !== null ? c.brace
            : offset !== undefined ? (bracedBase ? base : sum === 0 ? 0 : offset + sum) // a cancelling sum reads the default (absent)
            : c.total !== 0 ? (base === 0 && CPI_FAMILY_BASELINE[family] !== undefined ? CPI_FAMILY_BASELINE[family]! : base) + c.total : base;
        if (offset !== undefined && next === base && c.total === 0 && c.brace === null) continue; // nothing to fold
        // the record is written whenever contribution rows are (a zero delta too): an absent record means "recover it from the rows"
        if (next !== base) setModifier(creature, family, next);
        if (next !== base || offset !== undefined) syncDerived(creature, family); // the arcane mirror follows even an unchanged value
        record[family] = next - base;
        if (SKILL_GLOBAL_FAMILIES.includes(family)) applySkillGlobals(creature, family, c.sources);
        setSources(creature, family, [...existing, ...c.sources]); // kept when contributions cancel, so the total stays explainable
    }
    if (Object.keys(record).length) creature[CPI_RECORD_KEY] = record; else delete creature[CPI_RECORD_KEY];
    return record;
}

/** Strip + re-collect + re-apply on a detached copy of `row`'s computed.creature. `changed` = the folded cells differ from the row's. */
export function refoldPassiveIntegers(row: Row, deps: PassiveIntegerFoldDependencies, families: readonly string[] = CPI_FOLD_FAMILIES): { creature: Row; collection: PassiveIntegerCollection; cells: Record<string, unknown>; changed: boolean } {
    const prior = isObj(row.computed) && isObj((row.computed as Row).creature) ? (row.computed as Row).creature as Row : {};
    const creature = JSON.parse(JSON.stringify(prior)) as Row;
    families = normalizeFamilies(families);
    const collection = collectPassiveIntegerContributions(row, deps, families);
    // Unavailable capacity inputs leave this family's served value and record
    // intact while every other family can still settle.
    const completeFamilies = collection.capacity?.incomplete ? families.filter(f => f !== CPI_CAPACITY_FAMILY) : families;
    stripPassiveIntegerFold(creature, completeFamilies);
    applyPassiveIntegerFold(creature, collection, completeFamilies);
    const cells = passiveIntegerCells(creature, families);
    const priorCells = passiveIntegerCells(prior, families);
    // the speeds cell rides only when a speed fold is involved on either side (a removed fold must still be written back)
    if ('computed.creature.speeds' in priorCells && !('computed.creature.speeds' in cells)) cells['computed.creature.speeds'] = creature.speeds ?? null;
    if ('computed.creature.speeds' in cells && !('computed.creature.speeds' in priorCells)) priorCells['computed.creature.speeds'] = prior.speeds ?? null;
    if ('computed.creature.skills' in priorCells && !('computed.creature.skills' in cells)) cells['computed.creature.skills'] = creature.skills ?? null;
    if ('computed.creature.skills' in cells && !('computed.creature.skills' in priorCells)) priorCells['computed.creature.skills'] = prior.skills ?? null;
    if ('computed.creature.capacity' in priorCells && !('computed.creature.capacity' in cells)) cells['computed.creature.capacity'] = creature.capacity ?? null;
    if ('computed.creature.capacity' in cells && !('computed.creature.capacity' in priorCells)) priorCells['computed.creature.capacity'] = prior.capacity ?? null;
    const changed = JSON.stringify(cells) !== JSON.stringify(priorCells);
    return { creature, collection, cells, changed };
}

/** The C-PI cells of a computed.creature, as the heal posts them: dotted path → value (null = absent). Canonical key order. */
export function passiveIntegerCells(creature: Row, families: readonly string[] = CPI_FOLD_FAMILIES): Record<string, unknown> {
    const cells: Record<string, unknown> = {};
    const mods = isObj(creature.modifiers) ? creature.modifiers as Row : {};
    const srcs = isObj(creature.modifier_sources) ? creature.modifier_sources as Row : {};
    const ac = isObj(creature.ac) ? creature.ac as Row : {};
    if (families.some(f => CPI_AC_FAMILIES.includes(f))) for (const k of ['ac', 'dr', 'total_mod']) cells[`computed.creature.ac.${k}`] = ac[k] ?? null;
    if (families.includes(CPI_MITIGATION_FAMILY)) { cells['computed.creature.ac.mitigation'] = ac.mitigation ?? null; cells['computed.creature.damage_mitigation'] = creature.damage_mitigation ?? null; }
    const record = isObj(creature[CPI_RECORD_KEY]) ? creature[CPI_RECORD_KEY] as Row : {};
    const speedFolded = [...CPI_SPEED_FAMILIES, CPI_JUMP_FAMILY, CPI_SPEED_SHIELD_FAMILY].some(f => f in record) || (isObj(creature.speeds) && /"fold":"cpi"|"shield_prior":/.test(JSON.stringify(creature.speeds)));
    if (families.some(f => CPI_SPEED_FAMILIES.includes(f) || f === CPI_JUMP_FAMILY || f === CPI_SPEED_SHIELD_FAMILY) && speedFolded) cells['computed.creature.speeds'] = creature.speeds ?? null;
    const skillFolded = SKILL_GLOBAL_FAMILIES.some(f => f in record) || (isObj(creature.skills) && JSON.stringify(creature.skills).includes('"fold":"cpi"'));
    if (families.some(f => SKILL_GLOBAL_FAMILIES.includes(f)) && skillFolded) cells['computed.creature.skills'] = creature.skills ?? null;
    // family 7 rides only when the capacity is scaled (the record, or a stamp on the cluster itself) on this side
    if (families.includes(CPI_CAPACITY_FAMILY) && (CPI_CAPACITY_FAMILY in record || compiledCapacityFactor(creature.capacity) !== 1)) cells['computed.creature.capacity'] = creature.capacity ?? null;
    for (const family of [...families].filter(f => CPI_MODIFIER_FAMILIES.includes(f)).sort()) {
        cells[`computed.creature.modifiers.${family}`] = mods[family] ?? null;
        cells[`computed.creature.modifier_sources.${family}`] = srcs[family] ?? null;
    }
    if (families.includes('max_summons')) { const meta = isObj(creature.meta) ? creature.meta as Row : {}; cells['computed.creature.meta.max_summons'] = meta.max_summons ?? null; }
    if (families.includes('arcane_threshold')) { const sc = isObj(creature.spellcasting) ? creature.spellcasting as Row : {}; cells['computed.creature.spellcasting.arcane_threshold'] = sc.arcane_threshold ?? null; }
    cells[`computed.creature.${CPI_RECORD_KEY}`] = isObj(creature[CPI_RECORD_KEY]) ? creature[CPI_RECORD_KEY] : null;
    return cells;
}

// ---- family 1: AC + damage mitigation (update_ac_values' parts) ----------------------------------------------------------------------
// ac: total_mod / ac.total / dr move by the passive share (calc_ec_ac: ac = ec + total_mod; dr = (ac ?: 10) − 10). A passive the PHP display
// already lists (a PC's legacy passive slot is walked by collect_ac_mod_*_sources_for_display; the total never counted it) gets no second
// row — the stored rows now sum to the total. Fold rows carry `fold: 'cpi'`; the breakdown moves with them.
const partRows = (part: Row): Row[] => Array.isArray(part.sources) ? (part.sources as unknown[]).filter(isObj) : [];
function setScalarPart(ac: Row, key: string, value: number): void { if (value === 0) delete ac[key]; else ac[key] = value; } // ft_compute_persist zero-elision
function bumpBreakdown(part: Row, category: string, delta: number): void {
    if (delta === 0) return;
    const b: Row = isObj(part.breakdown) ? part.breakdown as Row : {};
    const next = int(b[category]) + delta;
    const stillListed = partRows(part).some(r => String(r.category ?? r.kind ?? '') === category);
    if (next === 0 && !stillListed) delete b[category]; else b[category] = next;
    if (Object.keys(b).length) part.breakdown = b; else delete part.breakdown;
}
/** An unsuppressed fixed equipped value (`{x}`, an inventory_equipped row with value_brace) overrides everything else — no fold. */
const equippedLocked = (rows: Row[]): boolean => rows.some(r => String(r.kind ?? '') === 'inventory_equipped' && r.value_brace !== undefined && r.value_brace !== null && !phpJsonTruthy(r.suppressed));
const listedPassive = (rows: Row[], href: string, category: string): boolean => href !== '' && rows.some(r => r.fold !== CPI_FOLD_MARK
    && String(r.href_id ?? '').toUpperCase() === href.toUpperCase() && String(r.category ?? category) === category);
function stripAcFold(creature: Row, record: Row | null): void {
    const ac = isObj(creature.ac) ? creature.ac as Row : null;
    if (!ac) return;
    const delta = record && Object.prototype.hasOwnProperty.call(record, 'ac') ? int(record.ac) : 0;
    const part = isObj(ac.ac) ? ac.ac as Row : null;
    if (part) {
        const fold = partRows(part).filter(r => r.fold === CPI_FOLD_MARK);
        if (fold.length) { part.sources = partRows(part).filter(r => r.fold !== CPI_FOLD_MARK); for (const r of fold) bumpBreakdown(part, String(r.category), -int(r.value)); }
    }
    if (delta !== 0) {
        setScalarPart(ac, 'total_mod', int(ac.total_mod) - delta);
        if (part) part.total = int(part.total) - delta;
        setScalarPart(ac, 'dr', (part ? int(part.total) || 10 : 10) - 10);
    }
}
function applyAcFold(creature: Row, collection: PassiveIntegerCollection, record: Row): void {
    const lines = CPI_AC_FAMILIES.flatMap(f => (collection.byFamily[f]?.sources ?? []).filter(r => r.value_brace === undefined).map(r => ({ ...r, category: f }) as Row));
    const delta = lines.reduce((s, r) => s + int(r.value), 0);
    const ac = isObj(creature.ac) ? creature.ac as Row : null;
    const part = ac && isObj(ac.ac) ? ac.ac as Row : null;
    if (!lines.length || !ac || !part) return; // no AC cluster: nothing to fold into (an item-creature template)
    if (equippedLocked(partRows(part))) return;
    const existing = partRows(part);
    // (2026-10-07) a STATUS row the rebuild names (ft_ac_mod_status_source_rows: kind status_effect, href = the status ITEM id — a
    // passive applied as a status, or an aura's own anchor row — an aura DEFINITION yields no fold line here, the collector skips it) is the status's share, already in the total: it never
    // stands in for the held passive's line, or the folded delta would land with no row and the rows would stop summing to the total
    const passiveLines = existing.filter(r => r.kind !== 'status_effect');
    const added = lines.filter(r => !listedPassive(passiveLines, String(r.href_id ?? ''), String(r.category)));
    if (added.length) { part.sources = [...existing, ...added]; for (const r of added) bumpBreakdown(part, String(r.category), int(r.value)); }
    if (delta !== 0) {
        setScalarPart(ac, 'total_mod', int(ac.total_mod) + delta);
        part.total = int(part.total) + delta;
        setScalarPart(ac, 'dr', (int(part.total) || 10) - 10);
    }
    record.ac = delta;
}
// mitigation: ac.mitigation {total, base, locked, sources, breakdown} (persisted only when ≠ 0) + a PC's computed.creature.damage_mitigation
// {total}. PHP's display lists every passive the legacy slot holds and pads a remainder row ("Unlisted mitigation (equipped: …)" /
// "Other sources") so the lines sum to the chokepoint; an already-listed passive now counted moves that remainder instead of adding a
// row — the remainder row carries its own `fold_adjust` so the strip restores it exactly.
const isRemainderRow = (r: Row): boolean => String(r.category ?? '') === 'damage_mitigation'
    && (String(r.label ?? '').startsWith('Unlisted mitigation (equipped:') || String(r.label ?? '') === 'Other sources');
function mirrorDamageMitigation(creature: Row, isPc: boolean): void {
    if (!isPc) return;
    const total = isObj(creature.ac) && isObj((creature.ac as Row).mitigation) ? int(((creature.ac as Row).mitigation as Row).total) : 0;
    if (total !== 0) creature.damage_mitigation = { total }; else delete creature.damage_mitigation;
}
/** The record key that remembers a mitigation fold ran on a PC (the mirror must be restored by the strip even when the fold took it to 0). */
const MIT_PC_KEY = 'damage_mitigation_pc';
function stripMitigationFold(creature: Row, record: Row | null): void {
    const ac = isObj(creature.ac) ? creature.ac as Row : null;
    const mit = ac && isObj(ac.mitigation) ? ac.mitigation as Row : null;
    if (!ac || !mit) return;
    const delta = record && Object.prototype.hasOwnProperty.call(record, CPI_MITIGATION_FAMILY) ? int(record[CPI_MITIGATION_FAMILY]) : 0;
    const isPc = !!record && int(record[MIT_PC_KEY]) === 1;
    const rows = partRows(mit);
    const folded = rows.some(r => r.fold === CPI_FOLD_MARK || r.fold_adjust !== undefined);
    if (!folded && delta === 0) return;
    const kept: Row[] = [];
    let removed = 0;
    for (const r of rows) {
        if (r.fold === CPI_FOLD_MARK) { removed += int(r.value); continue; }
        if (r.fold_adjust !== undefined) {
            const adj = int(r.fold_adjust);
            const back: Row = { ...r, value: int(r.value) - adj };
            delete back.fold_adjust;
            removed += adj;
            if (int(back.value) !== 0) kept.push(back); // PHP never emits a zero line
            continue;
        }
        kept.push(r);
    }
    if (kept.length) mit.sources = kept; else delete mit.sources;
    // the breakdown moves AFTER the rows: a category key stays only while a row of that category remains (PHP sums per listed category)
    const b: Row = isObj(mit.breakdown) ? mit.breakdown as Row : {};
    if (removed !== 0 || 'damage_mitigation' in b) {
        const next = int(b.damage_mitigation) - removed;
        if (kept.some(r => String(r.category ?? '') === 'damage_mitigation')) b.damage_mitigation = next; else if (next === 0) delete b.damage_mitigation; else b.damage_mitigation = next;
    }
    if (Object.keys(b).length) mit.breakdown = b; else delete mit.breakdown;
    mit.total = int(mit.total) - delta;
    if (int(mit.total) === 0 && !kept.length) delete ac.mitigation;
    if (isPc) mirrorDamageMitigation(creature, true);
}
function applyMitigationFold(creature: Row, collection: PassiveIntegerCollection, record: Row): void {
    const c = collection.byFamily[CPI_MITIGATION_FAMILY];
    const lines = (c?.sources ?? []).filter(r => r.value_brace === undefined).map(r => ({ ...r, category: 'damage_mitigation' }) as Row);
    const delta = lines.reduce((s, r) => s + int(r.value), 0);
    const ac = isObj(creature.ac) ? creature.ac as Row : null;
    if (!lines.length || !ac) return;
    const mit: Row = isObj(ac.mitigation) ? ac.mitigation as Row : { total: 0, base: 0, locked: false };
    const rows = partRows(mit);
    if (equippedLocked(rows)) return;
    const out: Row[] = [...rows];
    for (const line of lines) {
        if (!listedPassive(rows, String(line.href_id ?? ''), 'damage_mitigation')) { out.push(line); continue; }
        // already listed: the line stays as PHP shows it; the difference rides the remainder row (adjusted in place, reversibly) or, when
        // PHP needed none, a fold-owned "Other sources" row — never a second row attributed to the same passive
        const rem = out.findIndex(r => isRemainderRow(r) && r.fold !== CPI_FOLD_MARK);
        if (rem >= 0) { const r = out[rem]!; out[rem] = { ...r, value: int(r.value) + int(line.value), fold_adjust: int(r.fold_adjust) + int(line.value) }; }
        else out.push({ category: 'damage_mitigation', label: 'Other sources', value: int(line.value), href_id: '', kind: 'ac_mod_other', fold: CPI_FOLD_MARK });
    }
    if (out.length) mit.sources = out;
    const b: Row = isObj(mit.breakdown) ? mit.breakdown as Row : {};
    b.damage_mitigation = int(b.damage_mitigation) + delta; // a damage_mitigation row now exists, so the key stays (even at 0)
    mit.breakdown = b;
    mit.total = int(mit.total) + delta;
    // (a zero total keeps the part — PHP would omit it, but the strip must be able to restore PHP's rows; consumers read 0 either way)
    ac.mitigation = mit;
    mirrorDamageMitigation(creature, !!collection.isPc);
    record[CPI_MITIGATION_FAMILY] = delta;
    if (collection.isPc) record[MIT_PC_KEY] = 1;
}

// ---- family 2: monster speeds (computed.creature.speeds.<short>) -----------------------------------------------------------------------
// compute_speeds walks the LEGACY top-level passive slot: a PC keeps it (its passive speeds are live in PHP — never folded here), the monster
// bucket mirror empties it. On a NON-PC row the fold adds the passive's additive share to the speed's wrapper (created when absent) with a
// `fold:"cpi"` row; a locked wrapper, a braced / factor row, or a DERIVED swim / climb (½ walk — PHP's derivation semantics beside a
// dynamic source are unproven here) is left alone. When walking moves, a DERIVED jump (⅓ walk + 2 × Body) is re-derived; the strip
// re-derives it from the restored walk. The snapshot behind derived swim / climb excludes passives, so they never move with walking.
function speedWrapper(speeds: Row, short: string): Row | null {
    const w = speeds[short];
    if (w === undefined || w === null) return null;
    if (isObj(w)) return w as Row;
    // a lean int wrapper: expanded for the fold, restored as an int by the strip (Codex review)
    const expanded: Row = { total: int(w), base: 0, locked: false, fold_scalar: true };
    speeds[short] = expanded;
    return expanded;
}
const walkOf = (speeds: Row): number => isObj(speeds.walking) ? int((speeds.walking as Row).total) : int(speeds.walking);
function rederiveJump(creature: Row, speeds: Row): void {
    const jump = speeds.jumping;
    if (!isObj(jump) || (jump as Row).derived !== true) return;
    const walk = walkOf(speeds);
    if (walk <= 0) return; // (the fold never moves walking across zero — PHP's zero-walk branch drops / pins jump instead)
    const body = int(own(own(own(creature, 'essences'), 'body'), 'total'));
    const d = compileCreatureDerivedJump({ walk, body }) as unknown as Row;
    // keep any other key the stored wrapper carries (Codex review): only the derivation's own fields move
    speeds.jumping = { ...(jump as Row), total: d.total, sources: (d.sources as Row[]).map(r => ({ ...r })), breakdown: d.breakdown, derived: true };
}
function stripSpeedFold(creature: Row, record: Row | null): void {
    const speeds = isObj(creature.speeds) ? creature.speeds as Row : null;
    if (!speeds) return;
    let walkMoved = false;
    for (const family of CPI_SPEED_FAMILIES) {
        const short = family === 'teleport' ? 'teleport' : family.slice(6);
        if (!isObj(speeds[short])) continue;
        const w = speeds[short] as Row;
        const hasRecord = !!record && Object.prototype.hasOwnProperty.call(record, family);
        const rows = partRows(w);
        const fold = rows.filter(r => r.fold === CPI_FOLD_MARK);
        const removed = fold.reduce((s, r) => s + int(r.value), 0);
        // the record is the authority; a lost record is recovered from the fold rows (a speed share is purely additive)
        const delta = hasRecord ? int(record![family]) : removed;
        if (!fold.length && delta === 0 && w.fold_scalar !== true && w.fold_created !== true) continue;
        const kept = rows.filter(r => r.fold !== CPI_FOLD_MARK);
        if (kept.length) w.sources = kept; else delete w.sources;
        const b: Row = isObj(w.breakdown) ? w.breakdown as Row : {};
        if (removed !== 0 || 'passive_ability' in b) {
            const next = int(b.passive_ability) - removed;
            if (kept.some(r => String(r.category ?? r.kind ?? '') === 'passive_ability')) b.passive_ability = next; else if (next === 0) delete b.passive_ability; else b.passive_ability = next;
        }
        if (Object.keys(b).length) w.breakdown = b; else delete w.breakdown;
        w.total = int(w.total) - delta;
        if (w.fold_created === true) { delete w.fold_created; if (!kept.length && int(w.total) === 0) { delete speeds[short]; if (family === 'speed_walking' && delta !== 0) walkMoved = true; continue; } }
        if (w.fold_scalar === true) speeds[short] = int(w.total);
        if (family === 'speed_walking' && delta !== 0) walkMoved = true;
    }
    if (walkMoved) rederiveJump(creature, speeds);
}
function applySpeedFold(creature: Row, collection: PassiveIntegerCollection, record: Row): void {
    if (collection.isPc) return;
    const speeds = isObj(creature.speeds) ? creature.speeds as Row : null;
    if (!speeds) return;
    let walkMoved = false;
    for (const family of CPI_SPEED_FAMILIES) {
        const c = collection.byFamily[family];
        if (!c || c.brace !== null) continue; // a passive {N} on a speed is not modelled: left to PHP's (absent) walk
        // speed_cannot_be_reduced held: a passive's negative share is a reduction and is ignored
        const lines = c.sources.filter(r => r.value_brace === undefined && !(collection.speedShield && int(r.value) < 0));
        if (!lines.length) continue;
        const short = family === 'teleport' ? 'teleport' : family.slice(6);
        const delta = lines.reduce((s, r) => s + int(r.value), 0);
        const existing = isObj(speeds[short]) ? speeds[short] as Row : null;
        if (existing && (existing.locked === true || partRows(existing).some(r => r.value_brace !== undefined || r.value_factor !== undefined))) continue;
        if (existing && (short === 'swimming' || short === 'climbing') && existing.derived === true) continue;
        // walking never crosses zero here: PHP's zero-walk branch drops / pins jump and moves the derived speeds' basis
        if (family === 'speed_walking' && (walkOf(speeds) <= 0 || walkOf(speeds) + delta <= 0)) continue;
        let w = speedWrapper(speeds, short);
        const created = !w;
        if (!w) { w = { total: 0, base: 0, locked: false, fold_created: true }; speeds[short] = w; }
        if (created && delta === 0) { delete speeds[short]; continue; }
        w.sources = [...partRows(w), ...lines];
        const b: Row = isObj(w.breakdown) ? w.breakdown as Row : {};
        b.passive_ability = int(b.passive_ability) + delta;
        w.breakdown = b;
        w.total = int(w.total) + delta;
        record[family] = delta;
        if (family === 'speed_walking' && delta !== 0) walkMoved = true;
    }
    if (walkMoved) rederiveJump(creature, speeds);
}

// ---- family 4: skill adv / dis globals (computed.creature.skills.<skill>) ---------------------------------------------------------------
// The roll path reads skill_rolls_adv / _dis through the modifiers bridge (skill_roll_inc.php) and SKIPS the compiled lines tagged
// `global_layer` — so the modifiers fold is what the roll uses. build_caster_skills also folds each global into every skill's
// adv_dis_stacks with a `global_layer` line (ft_skill_global_status_lines); the fold adds its passive lines the same way (marked
// `fold:"cpi"`) so the sheet matches the roll, and the strip takes exactly those back.
const SKILL_GLOBAL_FAMILIES = ['skill_rolls_adv', 'skill_rolls_dis'];
function skillEntries(creature: Row): Array<[string, Row]> {
    const skills = isObj(creature.skills) ? creature.skills as Row : null;
    if (!skills) return [];
    return Object.entries(skills).filter(([k, v]) => k !== '_meta' && isObj(v)) as Array<[string, Row]>;
}
function stripSkillGlobals(creature: Row): void {
    for (const [, entry] of skillEntries(creature)) {
        const bd = isObj(entry.adv_dis_breakdown) ? entry.adv_dis_breakdown as Row : null;
        const lines = bd && Array.isArray(bd.lines) ? (bd.lines as unknown[]).filter(isObj) : [];
        const fold = lines.filter(l => l.fold === CPI_FOLD_MARK);
        if (!fold.length) continue;
        const kept = lines.filter(l => l.fold !== CPI_FOLD_MARK);
        const stacks = int(entry.adv_dis_stacks) - fold.reduce((s, l) => s + int(l.value), 0);
        if (kept.length) bd!.lines = kept; else delete entry.adv_dis_breakdown;
        if (stacks === 0 && !kept.length) delete entry.adv_dis_stacks; else entry.adv_dis_stacks = stacks;
    }
}
function applySkillGlobals(creature: Row, family: string, rows: Row[]): void {
    const sign = family === 'skill_rolls_dis' ? -1 : 1;
    const add = rows.filter(r => r.value_brace === undefined && int(r.value) !== 0)
        .map(r => ({ value: sign * int(r.value), label: String(r.label ?? ''), kind: 'adv_dis', href_id: String(r.href_id ?? '') || null, global_layer: family, fold: CPI_FOLD_MARK } as Row));
    if (!add.length) return;
    for (const [, entry] of skillEntries(creature)) {
        const bd: Row = isObj(entry.adv_dis_breakdown) ? entry.adv_dis_breakdown as Row : {};
        const lines = Array.isArray(bd.lines) ? (bd.lines as unknown[]).filter(isObj) : [];
        bd.lines = [...lines, ...add.map(l => ({ ...l }))];
        entry.adv_dis_breakdown = bd;
        entry.adv_dis_stacks = int(entry.adv_dis_stacks) + add.reduce((s, l) => s + int(l.value), 0);
    }
}

// ---- family 6: jumping (computed.creature.speeds.jumping) ------------------------------------------------------------------------------
// The PC/NPC speed leaf supplies an unmultiplied native host/form base. This fold
// owns held dynamic jump sources and multipliers once; strip/refold preserves that
// base across deltas. No PHP-populated jump cluster is required.
export interface JumpSourceRow { label: string; href_id: string; kind: string; token: string; layer: 'speed_jumping' | 'speed_jumping_multiplier' }
export interface JumpSources { rows: JumpSourceRow[]; incomplete: boolean; missing: string[] }
type JumpReaders = { layerEntries: (row: ItemFieldRow | null, name: string) => ItemFieldRow; itemField: (row: ItemFieldRow | null, name: string) => unknown; layerValue: (row: ItemFieldRow | null, name: string) => unknown };
function collectJumpSources(host: Row, passives: Array<{ entry: unknown; def: ItemFieldRow; gear: boolean; feat: boolean }>, context: JumpReaders,
    query: (id: string) => ItemFieldRow | null): JumpSources {
    const rows: JumpSourceRow[] = [];
    let incomplete = false;
    const missing = new Set<string>();
    const miss = (id: string): void => { incomplete = true; if (validUuid(id)) missing.add(id); };
    const nameOf = (def: ItemFieldRow): string => phpJsonString(context.itemField(def, 'item_name') ?? '');
    const idOfDef = (def: ItemFieldRow): string => upper(phpJsonString(context.itemField(def, 'item_id') ?? ''));
    const take = (def: ItemFieldRow | null, kind: string, fallbackId = '', layers: readonly JumpSourceRow['layer'][] = ['speed_jumping', 'speed_jumping_multiplier']): void => {
        if (!def) return;
        for (const layer of layers) {
            for (const frag of Object.values(context.layerEntries(def, layer))) {
                const token = readLayerScoringToken(frag);
                if (token === null || token.trim() === '') continue;
                // "{[speed_jumping]}" keeps the current jump — a no-op lock (Spilled Oil of Slipperiness). It is no source: a lock-only set
                // must not create a fold record (N4c oracle 2026-10-02: the legacy rebuild leaves no cpi.speed_jumping for it)
                if (layer === 'speed_jumping' && /^\{\[speed_jumping\]\}$/.test(token.trim())) continue;
                rows.push({ label: nameOf(def) || 'Unknown source', href_id: idOfDef(def) || fallbackId, kind, token: token.trim(), layer });
            }
        }
    };
    // Authored jumping is already in the native base; host multipliers are not.
    take(host, 'host', '', ['speed_jumping_multiplier']);
    for (const c of passives) take(c.def, c.gear ? 'inventory_equipped' : c.feat ? 'feat' : 'passive_ability');
    // statuses (target): each status's own layers
    const statusRows = Object.values(readCreaturePayloadStatuses(host, context.layerValue, query)).filter(arrayLike);
    let formPending = true;
    for (const st of statusRows) {
        const sid = upper(phpJsonString(own(st, 'status_item_id') ?? ''));
        const def = arrayLike(own(st, 'status_arr')) && arrayLike(own(own(st, 'status_arr'), 'item_layer_types')) ? own(st, 'status_arr') as ItemFieldRow : validUuid(sid) ? query(sid) : null;
        if (!def) { if (validUuid(sid)) miss(sid); continue; }
        // The selected creature status is the form's BASE, not a second jump
        // bonus. Its multiplier still participates in the highest-source gate.
        const selectedForm = formPending && context.itemField(st, 'item_type') === 'creature';
        if (selectedForm) formPending = false;
        take(def, 'status_effect', sid, selectedForm ? ['speed_jumping_multiplier'] : undefined);
    }
    // equipped rows: the template's own authored jump layers (its nested passives arrive through the passive bucket, gear-surfaced). LIVE
    // inventory rows only — a monster's root inventory_equipped list is its template's static starting loadout, not what it wears
    const walkInv = (list: unknown): void => {
        for (const inst of (Array.isArray(list) ? list : isObj(list) ? Object.values(list) : []).filter(isObj)) {
            if (readItemUuidSlot(readInventoryField(inst as never, 'equipped') ?? '') !== '') {
                const tid = upper(phpJsonString(readInventoryField(inst as never, 'inventory_item_id') ?? ''));
                if (validUuid(tid)) { const tpl = query(tid); if (!tpl) miss(tid); else take(tpl, 'inventory_equipped'); }
            }
            walkInv(inst.inventory_in_container);
        }
    };
    walkInv(own(host, 'inventory'));
    // feats: each feat's own layers (its granted passives are in the passive bucket, ft_granted_by_feat)
    const featEntries = [...Object.values(context.layerEntries(host as ItemFieldRow, 'feat'))];
    const relocated = own(own(own(own(host, 'computed'), 'creature'), 'abilities'), 'feat');
    if (arrayLike(relocated)) for (const e of Object.values(relocated)) if (!featEntries.includes(e)) featEntries.push(e);
    const seenFeat = new Set<string>();
    for (const e of featEntries) {
        const fid = upper(typeof e === 'string' ? e : phpJsonString(own(e, 'item_id') ?? own(e, 'ability_id') ?? ''));
        if (!validUuid(fid) || seenFeat.has(fid)) continue;
        seenFeat.add(fid);
        const def = query(fid);
        if (!def) { miss(fid); continue; }
        take(def, 'feat');
    }
    return { rows, incomplete, missing: [...missing] };
}
/** Resolve dynamic rows over the native unmultiplied jump total. Null when a token is not modelled (the family is left unfolded). */
export function resolveJump(baseTotal: number, walk: number, src: JumpSourceRow[], shield = false): { total: number; rows: Row[] } | null {
    // speed_cannot_be_reduced held (Wizard v9): a negative "+N", a slow factor and a lock BELOW the unlocked jump are ignored
    if (shield) {
        const reduces = (r: JumpSourceRow): boolean => {
            const t = r.token; let m: RegExpExecArray | null;
            if (r.layer === 'speed_jumping_multiplier') return Number(t) < 1;
            if ((m = /^([+-]?\d+)$/.exec(t))) return Number(m[1]) < 0;
            if ((m = /^(\d*\.?\d+)\s*\*\s*\[speed_jumping\]$/.exec(t) ?? /^\[speed_jumping\]\s*\*\s*(\d*\.?\d+)$/.exec(t))) return Number(m[1]) < 1;
            return false;
        };
        const kept = src.filter(r => !reduces(r));
        const isLock = (r: JumpSourceRow): boolean => r.layer === 'speed_jumping' && /^\{-?\d+\}$/.test(r.token);
        const locked = resolveJump(baseTotal, walk, kept);
        if (!kept.some(isLock)) return locked;
        const open = resolveJump(baseTotal, walk, kept.filter(r => !isLock(r)));
        return locked && open && open.total > locked.total ? open : locked;
    }
    const out: CreatureSpeedSource[] = [];
    let brace: number | null = null;
    const floors: Array<{ row: CreatureSpeedSource; at: number }> = [];
    for (const r of src) {
        const t = r.token;
        let m: RegExpExecArray | null;
        const base = { category: r.kind, label: r.label, href_id: r.href_id, kind: r.kind };
        if (r.layer === 'speed_jumping_multiplier') {
            const k = Math.trunc(Number(t));
            if (!Number.isFinite(k)) return null;
            if (k <= 1) continue;
            out.push({ ...base, value: 0, value_factor: k } as CreatureSpeedSource);
        } else if ((m = /^\{(-?\d+)\}$/.exec(t))) {
            const b = Number(m[1]); brace = brace === null ? b : Math.max(brace, b);
            out.push({ ...base, value: 0, value_brace: b } as CreatureSpeedSource);
        } else if (/^\{\[speed_jumping\]\}$/.test(t)) {
            continue; // "keep the current jump" — a no-op lock
        } else if ((m = /^([+-]?\d+)$/.exec(t))) {
            out.push({ ...base, value: Number(m[1]) } as CreatureSpeedSource);
        } else if ((m = /^(?:(\d*\.?\d+)\s*\*\s*)?\[speed_walking\]$/.exec(t) ?? /^\[speed_walking\]\s*\*\s*(\d*\.?\d+)$/.exec(t))) {
            // a walk reference is a FLOOR ("your jump equals twice your walking speed" — Hasted, Running Leap): AT LEAST c × walk (rounded
            // up), never added; one coefficient only (a chained expression is unmodelled — Codex review)
            const c = Number(m[1] ?? 1);
            floors.push({ row: { ...base, value: 0 } as CreatureSpeedSource, at: Math.ceil(c * walk) });
        } else if ((m = /^(\d*\.?\d+)\s*\*\s*\[speed_jumping\]$/.exec(t) ?? /^\[speed_jumping\]\s*\*\s*(\d*\.?\d+)$/.exec(t))) {
            out.push({ ...base, value: 0, value_factor: Number(m[1]) } as CreatureSpeedSource);
        } else {
            return null;
        }
    }
    // Additive equipped jump sources share the speed cap: first highest wins.
    const additiveGear = out.filter(row => row.kind === 'inventory_equipped'
        && row.value_factor === undefined && row.value_brace === undefined);
    const [cappedGear] = capInventorySourceRows(additiveGear);
    const cappedRows = Object.values(cappedGear) as unknown as CreatureSpeedSource[];
    let gearIndex = 0;
    for (let index = 0; index < out.length; index++) if (additiveGear.includes(out[index]!))
        out[index] = cappedRows[gearIndex++]!;
    const additive = out.reduce((sum, row) => sum + row.value, 0);
    if (brace !== null) {
        // a bracket lock beats every other row: the brace row carries the whole difference so the rows still sum to the total
        const rows = [...out, ...floors.map(x => x.row)].map(r => ({ ...r, value: 0 })) as Row[];
        const i = rows.findIndex(r => r.value_brace === brace);
        rows[i]!.value = brace - baseTotal;
        return { total: brace, rows };
    }
    const naive = baseTotal + additive;
    const f = applyCreatureSpeedFactorRows(out, naive, false);
    const best = floors.reduce<{ row: CreatureSpeedSource; at: number } | null>((m, x) => !m || x.at > m.at ? x : m, null);
    let total = f.total;
    if (best && best.at > total) { best.row.value = best.at - total; total = best.at; }
    out.push(...floors.map(x => x.row));
    // the factor helper floors the total at 0: the rows must still sum to the delta (a lost record is recovered from them — Codex review)
    const residual = (total - baseTotal) - out.reduce((s, r) => s + Math.trunc(Number(r.value) || 0), 0);
    if (residual !== 0) out.push({ category: 'derived', label: 'Minimum jump distance (0)', value: residual, href_id: '', kind: 'speed_jumping' } as CreatureSpeedSource);
    return { total, rows: out as unknown as Row[] };
}
function stripJumpFold(creature: Row): void {
    const speeds = isObj(creature.speeds) ? creature.speeds as Row : null;
    const w = speeds && isObj(speeds.jumping) ? speeds.jumping as Row : null;
    if (!w) return;
    const rows = partRows(w);
    const fold = rows.filter(r => r.fold === CPI_FOLD_MARK);
    // A native speed compile may replace this cluster before the prior CPI record
    // is stripped. Only a wrapper carrying fold rows owns that old jump delta.
    if (!fold.length) return;
    const delta = fold.reduce((sum, row) => sum + int(row.value), 0);
    const kept = rows.filter(r => r.fold !== CPI_FOLD_MARK);
    if (kept.length) w.sources = kept; else delete w.sources;
    const b: Row = isObj(w.breakdown) ? w.breakdown as Row : {};
    for (const r of fold) { const k = String(r.category ?? r.kind ?? 'other'); const next = int(b[k]) - int(r.value); if (next === 0 && !kept.some(x => String(x.category ?? x.kind ?? '') === k)) delete b[k]; else b[k] = next; }
    if (Object.keys(b).length) w.breakdown = b; else delete w.breakdown;
    w.total = int(w.total) - delta;
    if (w.fold_jump_unlocked === true) { w.locked = false; delete w.fold_jump_unlocked; }
}
function applyJumpFold(creature: Row, collection: PassiveIntegerCollection, record: Row, refuseUnmodelled = false): void {
    const src = collection.jump;
    if (!src) return;
    const speeds = isObj(creature.speeds) ? creature.speeds as Row : null;
    const w = speeds && isObj(speeds.jumping) ? speeds.jumping as Row : null;
    if (!speeds || !w) return; // native zero-walk selection elides or locks the slot
    const walk = walkOf(speeds);
    if (walk <= 0) return;
    // Native leaves ignore null definitions, including declared-deleted feats and
    // equipment. The from-base fold owns unavailable-definition diagnostics.
    if (src.incomplete && !refuseUnmodelled) return;
    if (!src.rows.length) return;
    const resolved = resolveJump(int(w.total), walk, src.rows, collection.speedShield === true);
    if (!resolved) {
        if (refuseUnmodelled) throw new Error('speed-jump-token-unmodelled');
        return;
    }
    const delta = resolved.total - int(w.total);
    if (resolved.rows.some(row => row.value_brace !== undefined) && w.locked !== true) {
        w.fold_jump_unlocked = true;
        w.locked = true;
    }
    const rows = resolved.rows.map(r => ({ ...r, fold: CPI_FOLD_MARK }) as Row);
    w.sources = [...partRows(w), ...rows];
    const b: Row = isObj(w.breakdown) ? w.breakdown as Row : {};
    for (const r of rows) { const k = String(r.category ?? r.kind ?? 'other'); b[k] = int(b[k]) + int(r.value); }
    w.breakdown = b;
    w.total = resolved.total;
    record[CPI_JUMP_FAMILY] = delta;
}

// ---- family 7: carrying capacity (computed.creature.capacity) --------------------------------------------------------------------------
// The compiled parts are (int)(size weight_multiplier × (base + Body)); the fold scales and truncates each by the collected factor,
// stamped in the part's breakdown (`carrying_capacity_multiplier`) — the stamp, not only the record, is the strip's authority, so a lost record (or
// a cluster another claim installed) is still taken back exactly. `remaining` follows the new max.
function stripCapacityFold(creature: Row): void {
    if (compiledCapacityFactor(creature.capacity) === 1) return;
    const out = rescaleCompiledCapacity(creature.capacity, 1);
    if (out) creature.capacity = out;
}
function applyCapacityFold(creature: Row, collection: PassiveIntegerCollection, record: Row): void {
    const c = collection.capacity;
    if (!c || c.incomplete || c.factor === 1) return;
    const out = rescaleCompiledCapacity(creature.capacity, c.factor);
    if (!out) return; // no compiled capacity (or not its shape): nothing to scale
    creature.capacity = out;
    record[CPI_CAPACITY_FAMILY] = c.factor;
}

// ---- family 8: the speed shield (computed.creature.speeds, wholesale) ------------------------------------------------------------------
const canon = (v: unknown): string => Array.isArray(v) ? `[${v.map(canon).join(',')}]`
    : isObj(v) ? `{${Object.keys(v).sort().map(k => JSON.stringify(k) + ':' + canon(v[k])).join(',')}}` : JSON.stringify(v ?? null);
function applySpeedShieldFold(creature: Row, collection: PassiveIntegerCollection, record: Row): void {
    const s = collection.shieldSpeeds;
    const current = isObj(creature.speeds) ? creature.speeds as Row : null;
    if (!s || !current) return;
    // the parity guard, PER WRAPPER (2026-10-09 rules smoke: a Transmutation wizard's Stonewalker climbing '+[speed_walking]' is a cross-speed
    // passive the twin scores 0, so a whole-cluster guard refused the shield on every Transmutation 40 — walking stayed Slowed): only a
    // wrapper the UNSHIELDED twin reproduces exactly is re-derived (anything the twin does not model — a mount, a query-backed family, a
    // cross-speed passive — stays PHP's); a wrapper already carrying the shielded value (a C1 status claim) is left alone
    if (canon(current) === canon(s.shielded)) return;
    const out: Row = {};
    let replaced = false;
    for (const k of [...Object.keys(current), ...Object.keys(s.shielded).filter(k => !(k in current))]) {
        // a wrapper the shielded compile elides (Slowed's zero fly / burrow / hover kept only for its factor provenance) reads as an absent
        // speed = 0: it stays as a plain zero wrapper so the strip can restore the original
        const want = k in s.shielded ? s.shielded[k] : { total: 0, base: 0, locked: false }, had = current[k];
        if (canon(want) === canon(had) || canon(had) !== canon(s.unshielded[k])) { if (had !== undefined) out[k] = had; continue; }
        replaced = true;
        const w: Row = isObj(want) ? JSON.parse(JSON.stringify(want)) as Row : { total: int(want), base: 0, locked: false, shield_scalar: true };
        w.shield_prior = had === undefined ? null : JSON.parse(JSON.stringify(had));
        out[k] = w;
    }
    if (!replaced) return;
    creature.speeds = out;
    record[CPI_SPEED_SHIELD_FAMILY] = 1;
}
/** Restore every wrapper the shield replaced (its `shield_prior`; null = the wrapper did not exist). Record-independent. */
function stripSpeedShieldFold(creature: Row): void {
    const speeds = isObj(creature.speeds) ? creature.speeds as Row : null;
    if (!speeds || !Object.values(speeds).some(w => isObj(w) && 'shield_prior' in w)) return;
    const out: Row = {};
    for (const [k, w] of Object.entries(speeds)) {
        if (isObj(w) && 'shield_prior' in w) { if (w.shield_prior !== null) out[k] = w.shield_prior; continue; }
        out[k] = w;
    }
    creature.speeds = out;
}

/** Finish the native PC/NPC jump leaf using the same held-source collector and
 * fold as incremental CPI. Own only this jump wrapper; keep the host borrowed.
 * Fold rows carry their exact delta so a later finalization strips/reapplies once
 * even when the creature still has a record from a prior speed cluster.
 */
export function foldNativeCreatureJump(host: ItemFieldRow, deps: PassiveIntegerFoldDependencies,
    jump: Row, walk: number, shield: boolean): Row {
    const collection = collectPassiveIntegerContributions(host as Row, deps, [CPI_JUMP_FAMILY]);
    const creature: Row = { speeds: { walking: walk, jumping: jump } };
    applyJumpFold(creature, { ...collection, speedShield: shield }, {}, true);
    return jump;
}
